import { useState, useRef, useEffect, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../i18n';
import { SPECIAL_HOURS_ICONS, DEFAULT_ICON } from '../specialHoursIcons';
import { renderNote } from './specialHoursNote';

// `type` is the resolved special-hours type definition (name, icon, color, note)
// looked up by the parent from config.specialHourTypes. The badge is pure
// presentation: it renders the icon/label and a hover/click popover with the note.
export function SpecialHoursBadge({ special, type, placement = 'top' }) {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ left: r.left, top: r.top });
  }, []);

  const show = open || hover;

  useEffect(() => {
    if (!show) { setCoords(null); return; }
    measure();
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [show, measure]);

  if (!special || !type) return null;

  const Icon = SPECIAL_HOURS_ICONS[type.icon] || SPECIAL_HOURS_ICONS[DEFAULT_ICON];
  const label = type.name;
  const note = type.note?.[lang] || type.note?.en || '';
  const color = type.color || '#C8A96E';

  return (
    <span className="lr-sh-badge-wrap" ref={ref}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" className="lr-sh-badge" data-testid="special-hours-badge"
        aria-expanded={show} aria-describedby={show && note ? popId : undefined}
        aria-label={`${label} — ${t('specialHoursMoreInfo')}`}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ color }}>
        <Icon color={color} />
        <span className="lr-sh-badge-text">{label}</span>
      </button>
      {show && note && coords && createPortal(
        <span id={popId} role="tooltip" className="lr-sh-popover fixed" data-testid="special-hours-note"
          style={{ left: coords.left, top: coords.top - 8, transform: 'translateY(-100%)' }}>
          {renderNote(note)}
        </span>,
        document.body
      )}
    </span>
  );
}
